import React, { useState, useEffect } from "react";
import { toast } from "sonner";
import { Mail, Phone, ShieldCheck, Building2 } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PhoneVerifyDialog } from "@/components/dialogs";
import { VerifBadge, PageLoading } from "@/components/common";

function Row({ icon: Icon, title, desc, done, children }) {
  return (
    <div className="flex items-start gap-3 rounded-xl border border-border bg-card p-4">
      <div className={`flex h-10 w-10 items-center justify-center rounded-xl ${done ? "bg-emerald-500/10 text-emerald-600" : "bg-secondary text-muted-foreground"}`}><Icon className="h-5 w-5" /></div>
      <div className="flex-1">
        <p className="font-medium">{title}</p>
        <p className="text-sm text-muted-foreground">{desc}</p>
        <div className="mt-2">{done ? <span className="text-sm font-semibold text-emerald-600">✓ Verified</span> : children}</div>
      </div>
    </div>
  );
}

export default function Verification() {
  const { user } = useAuth();
  const [status, setStatus] = useState(null);
  const [phoneOpen, setPhoneOpen] = useState(false);
  const [biz, setBiz] = useState({ business_id: "", abn: "", registered_name: "" });
  const [myBiz, setMyBiz] = useState([]);
  useEffect(() => {
    api.get("/verification/status").then(({ data }) => setStatus(data));
    api.get("/businesses", { params: { mine: true } }).then(({ data }) => setMyBiz(data.items));
  }, []);
  if (!user || !status) return <PageLoading />;
  const submitBiz = async () => {
    try { const { data } = await api.post("/verification/business", biz); toast.success(data.message); } catch (e) { toast.error(apiError(e)); }
  };
  return (
    <div className="mx-auto max-w-2xl space-y-4">
      <div>
        <h1 className="font-display text-2xl font-bold tracking-tight">Verification</h1>
        <p className="text-sm text-muted-foreground">Verification builds trust. Badges only appear after real verification — they don't guarantee behaviour.</p>
      </div>
      <Row icon={Mail} title="Email Verified" desc="Confirms you own your email address." done={status.email_verified} />
      <Row icon={Phone} title="Phone Verified" desc="Required to sell, post a business or offer a service." done={status.phone_verified}>
        <Button size="sm" onClick={() => setPhoneOpen(true)} data-testid="verify-phone-btn">Verify phone</Button>
      </Row>
      <Row icon={ShieldCheck} title="Identity Verified" desc="Optional, risk-based identity check via a secure provider." done={status.identity_verified}>
        <span className="text-sm text-muted-foreground">Available on request for higher-risk activity.</span>
      </Row>
      <Row icon={Building2} title="Business Verified (ABN)" desc="Submit your ABN and registered name for admin review." done={false}>
        <div className="space-y-2">
          <Select value={biz.business_id} onValueChange={(v) => setBiz({ ...biz, business_id: v })}>
            <SelectTrigger data-testid="verify-biz-select"><SelectValue placeholder="Select your business" /></SelectTrigger>
            <SelectContent>{myBiz.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}{b.verified ? " ✓" : ""}</SelectItem>)}</SelectContent>
          </Select>
          <Input placeholder="ABN (11 digits)" value={biz.abn} onChange={(e) => setBiz({ ...biz, abn: e.target.value })} data-testid="verify-abn" />
          <Input placeholder="Registered business name" value={biz.registered_name} onChange={(e) => setBiz({ ...biz, registered_name: e.target.value })} data-testid="verify-regname" />
          <Button size="sm" onClick={submitBiz} disabled={!biz.business_id} data-testid="verify-biz-submit">Submit for review</Button>
        </div>
      </Row>
      <PhoneVerifyDialog open={phoneOpen} onOpenChange={setPhoneOpen} onVerified={() => api.get("/verification/status").then(({ data }) => setStatus(data))} />
    </div>
  );
}
